import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast, Toaster } from "sonner";
import { Image as ImageIcon, Plus, Trash2 } from "lucide-react";

export default function AdminSettings() {
  const [s, setS] = useState({});
  const [posters, setPosters] = useState([]);
  const [broadcast, setBroadcast] = useState({ title: "", body: "" });

  useEffect(() => {
    api.get("/settings").then(({data})=> {
      setS(data);
      setPosters(data.posters || []);
    });
  }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.patch("/admin/settings", patch);
      setS(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePosters = async () => {
    await save({ posters });
  };

  const sendBroadcast = async () => {
    if (!broadcast.title || !broadcast.body) return toast.error("Title and body required");
    try {
      await api.post("/admin/notice/broadcast", broadcast);
      toast.success("Broadcast sent");
      setBroadcast({ title: "", body: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rates = s.game_rates || {};

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Configuration</div>
        <h1 className="font-display font-bold text-3xl tracking-tight text-slate-900 mt-1">System Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Contact & Payment">
          <Field label="WhatsApp Country Code" value={s.whatsapp_country_code || "+91"} onSave={(v)=>save({ whatsapp_country_code: v })} testid="wa-cc" />
          <Field label="WhatsApp Number (with country code)" value={s.whatsapp_number || ""} onSave={(v)=>save({ whatsapp_number: v })} testid="whatsapp" />
          <Field label="Telegram Channel URL" value={s.telegram_url || ""} onSave={(v)=>save({ telegram_url: v })} testid="telegram" />
          <Field label="UPI ID (merchant)" value={s.upi_id || ""} onSave={(v)=>save({ upi_id: v })} testid="upi" />
          <Field label="UPI Payee Name" value={s.upi_payee_name || ""} onSave={(v)=>save({ upi_payee_name: v })} testid="upi-payee" />
          <Field label="QR Code Image URL" value={s.qr_code_url || ""} onSave={(v)=>save({ qr_code_url: v })} testid="qr" />
          <Field label="Min Deposit (points)" type="number" value={s.min_deposit ?? 300} onSave={(v)=>save({ min_deposit: parseInt(v) })} testid="min-dep" />
          <Field label="Min Withdraw (points)" type="number" value={s.min_withdraw ?? 500} onSave={(v)=>save({ min_withdraw: parseInt(v) })} testid="min-wd" />
          <Field label="Min Jodi Bet (points)" type="number" value={s.min_bet_jodi ?? 10} onSave={(v)=>save({ min_bet_jodi: parseInt(v) })} testid="min-bet-jodi" />
          <Field label="Min Haruf Bet (points)" type="number" value={s.min_bet_haruf ?? 10} onSave={(v)=>save({ min_bet_haruf: parseInt(v) })} testid="min-bet-haruf" />
          <Field label="Min Cross Bet — per jodi (points)" type="number" value={s.min_bet_cross ?? 10} onSave={(v)=>save({ min_bet_cross: parseInt(v) })} testid="min-bet-cross" />
        </Card>

        <Card title="Payment Gateway (IMB)">
          <p className="text-xs text-slate-500 mb-3">User ke "Proceed to Pay" par IMB gateway chalega. Neeche naya URL aur API key set karo.</p>
          <div className="p-3 rounded-xl border-2 border-blue-200 bg-blue-50/60">
            <div className="text-[11px] font-black text-blue-900 uppercase tracking-widest mb-2">IMB Gateway</div>
            <Field label="IMB Base URL" value={s.imb_base_url || "https://api.imbpay.in"} onSave={(v)=>save({ imb_base_url: v })} testid="imb-url" />
            <Field label="IMB API Key (user_token)" value={s.imb_user_token || ""} onSave={(v)=>save({ imb_user_token: v })} testid="imb-key" />
          </div>
        </Card>

        <Card title="Tutorial Videos">
          <p className="text-xs text-slate-500 mb-3">Apne mobile/laptop se **video file upload** karo (max 25MB). Yeh video app me users ko dikhega — "How to Play" section me + Deposit page pe inline play hoga.</p>
          <VideoField label="How to Play — Video" value={s.youtube_how_to_play || ""} onSave={(v)=>save({ youtube_how_to_play: v })} testid="yt-play" />
          <VideoField label="How to Deposit — Video" value={s.youtube_how_to_deposit || ""} onSave={(v)=>save({ youtube_how_to_deposit: v })} testid="yt-deposit" />
          <VideoField label="How to Withdraw — Video" value={s.youtube_how_to_withdraw || ""} onSave={(v)=>save({ youtube_how_to_withdraw: v })} testid="yt-withdraw" />
        </Card>

        <Card title="Deposit Bonus">
          <p className="text-xs text-slate-500 mb-3">Jab user ek particular amount ya usse jyada deposit kare, extra bonus wallet me automatically credit hota hai. Set 0 to disable.</p>
          <Field label="Bonus Threshold (₹)" type="number" value={s.deposit_bonus_threshold ?? 2000} onSave={(v)=>save({ deposit_bonus_threshold: parseInt(v) || 0 })} testid="bonus-threshold" />
          <Field label="Bonus % (extra)" type="number" value={s.deposit_bonus_percent ?? 5} onSave={(v)=>save({ deposit_bonus_percent: parseInt(v) || 0 })} testid="bonus-percent" />
          <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 mt-2">
            Example: threshold ₹{s.deposit_bonus_threshold ?? 2000} & bonus {s.deposit_bonus_percent ?? 5}% — user ₹2000 deposit karta hai → +₹{Math.floor(((s.deposit_bonus_threshold ?? 2000) * (s.deposit_bonus_percent ?? 5)) / 100)} extra wallet me.
          </div>
        </Card>

        <Card title="Refer & Earn">
          <p className="text-xs text-slate-500 mb-3">Jab koi referred user pehli deposit kare, referrer ko deposit ka % bonus milta hai. 0 set karne se bonus band ho jaayega.</p>
          <Field label="Referral % on First Deposit" type="number" value={s.referral_first_deposit_percent ?? 10} onSave={(v)=>save({ referral_first_deposit_percent: parseInt(v) || 0 })} testid="ref-percent" />
        </Card>

        <Card title="Withdrawal Rules">
          <p className="text-xs text-slate-500 mb-3">Admin controls for both — the time window within which users can request withdrawals AND the 24-hour "1 withdrawal per day" rule.</p>

          {/* 24h Cooldown toggle */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 mb-4" data-testid="wd-cooldown-toggle-row">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">24-Hour Cooldown Rule</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Agar ON hai to har user sirf 24 ghante me 1 baar withdrawal kar sakta hai. OFF karne par unlimited withdrawals.</div>
            </div>
            <button
              type="button"
              data-testid="wd-cooldown-toggle"
              onClick={() => save({ withdraw_cooldown_enabled: !(s.withdraw_cooldown_enabled !== false) })}
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                s.withdraw_cooldown_enabled === false ? "bg-slate-300" : "bg-emerald-500"
              }`}
              aria-pressed={s.withdraw_cooldown_enabled !== false}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${
                  s.withdraw_cooldown_enabled === false ? "translate-x-0" : "translate-x-5"
                }`}
              />
            </button>
          </div>

          {/* Time Window */}
          <div className="font-semibold text-slate-900 mb-1">Time Window (IST, 24-hour)</div>
          <p className="text-[11px] text-slate-500 mb-2">
            Users ye window ke andar hi withdrawal lagwa sakte hain. <b>24-hour format</b> — 6 PM = <code className="bg-slate-100 px-1 rounded">18:00</code>, 11:59 PM = <code className="bg-slate-100 px-1 rounded">23:59</code>. <b className="text-emerald-700">Dono fields khaali chhodo = 24×7 open rahega.</b>
          </p>
          <Field label="Open Time (HH:MM, 24-hr or blank)" value={s.withdraw_open_time || ""} onSave={(v)=>save({ withdraw_open_time: v })} testid="wd-open" />
          <Field label="Close Time (HH:MM, 24-hr or blank)" value={s.withdraw_close_time || ""} onSave={(v)=>save({ withdraw_close_time: v })} testid="wd-close" />
          <div className={`text-[11px] font-bold mt-1 ${(!s.withdraw_open_time || !s.withdraw_close_time) ? 'text-emerald-700' : 'text-slate-600'}`}>
            {(!s.withdraw_open_time || !s.withdraw_close_time)
              ? "✅ Currently: 24×7 withdrawals open"
              : `Currently: ${s.withdraw_open_time} → ${s.withdraw_close_time} IST`}
          </div>

          <div className="border-t border-slate-100 mt-5 pt-4">
            <div className="font-display font-semibold text-slate-900 mb-1">Auto Result API</div>
            <p className="text-xs text-slate-500 mb-3">Optional. Provide a webhook URL that returns today's market results. The Fetch button on Results page will hit it.</p>
            <Field label="Result API URL" value={s.result_api_url || ""} onSave={(v)=>save({ result_api_url: v })} testid="result-api" />
          </div>

          <div className="border-t border-slate-100 mt-5 pt-4">
            <div className="font-display font-semibold text-slate-900 mb-1">SMS / OTP Provider</div>
            <p className="text-xs text-slate-500 mb-3">Wire your own SMS gateway (MSG91 / Fast2SMS / Twilio etc.). Placeholders supported in payload: <code className="bg-slate-100 px-1 rounded">{`{mobile}`}</code> <code className="bg-slate-100 px-1 rounded">{`{message}`}</code> <code className="bg-slate-100 px-1 rounded">{`{api_key}`}</code> <code className="bg-slate-100 px-1 rounded">{`{sender}`}</code>. Leave empty to use demo mode (OTP shown in app).</p>
            <Field label="SMS API URL" value={s.sms_api_url || ""} onSave={(v)=>save({ sms_api_url: v })} testid="sms-url" />
            <Field label="SMS API Key" value={s.sms_api_key || ""} onSave={(v)=>save({ sms_api_key: v })} testid="sms-key" />
            <Field label="SMS Method (GET/POST)" value={s.sms_method || "GET"} onSave={(v)=>save({ sms_method: v.toUpperCase() })} testid="sms-method" />
            <Field label="Sender ID" value={s.sms_sender_id || ""} onSave={(v)=>save({ sms_sender_id: v })} testid="sms-sender" />
            <div className="mb-3">
              <Label className="text-xs uppercase tracking-wider text-slate-600">SMS Payload Template</Label>
              <Textarea data-testid="set-sms-payload" rows={2} value={s.sms_payload || ""} onChange={(e)=>setS({...s, sms_payload: e.target.value})} className="mt-1.5 font-mono text-xs" />
              <Button data-testid="save-sms-payload" onClick={()=>save({ sms_payload: s.sms_payload })} className="btn-brand h-9 mt-2 text-sm">Save Payload</Button>
            </div>
          </div>
        </Card>

        <Card title="Scrolling Notice & Broadcast">
          <Label>Notice Text</Label>
          <Textarea data-testid="notice-input" rows={4} value={s.notice_text || ""} onChange={(e)=>setS({...s, notice_text: e.target.value})} className="mt-1.5" />
          <Button data-testid="notice-save" onClick={()=>save({ notice_text: s.notice_text })} className="mt-2 btn-brand h-10">Save Notice</Button>

          <div className="border-t border-slate-100 mt-5 pt-4">
            <div className="font-display font-semibold text-slate-900 mb-2">Broadcast Notification</div>
            <Input data-testid="bc-title" placeholder="Title" value={broadcast.title} onChange={(e)=>setBroadcast({...broadcast, title: e.target.value})} className="mb-2" />
            <Textarea data-testid="bc-body" placeholder="Message body" rows={3} value={broadcast.body} onChange={(e)=>setBroadcast({...broadcast, body: e.target.value})} />
            <Button data-testid="bc-send" onClick={sendBroadcast} className="mt-2 bg-slate-900 hover:bg-slate-800 text-white h-10">Send to All</Button>
          </div>

          <div className="border-t border-slate-100 mt-5 pt-4">
            <div className="font-display font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <span>🔔</span>
              <span>"Allow Notifications" Popup</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              ON karo → purane users ko app khulte hi ek attractive popup dikhega jo unhein notification permission enable karne ko kahega.
              OFF karo → popup poori tarah band. <b className="text-slate-700">Naye users pe iska koi asar nahi</b> — unko APK native prompt aata hi hai.
            </p>
            <div
              className={`flex items-center justify-between rounded-xl px-4 py-3 border ${
                s.notification_popup_enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
              }`}
              data-testid="notif-popup-toggle-row"
            >
              <div>
                <div className="font-semibold text-sm text-slate-800">
                  Popup {s.notification_popup_enabled ? "ON" : "OFF"}
                </div>
                <div className="text-[11px] text-slate-500">
                  {s.notification_popup_enabled
                    ? "Purane users ko app khulte hi popup dikh raha hai"
                    : "Kisi user ko popup nahi dikh raha (default)"}
                </div>
              </div>
              <button
                data-testid="notif-popup-toggle"
                onClick={()=>save({ notification_popup_enabled: !s.notification_popup_enabled })}
                className={`relative w-14 h-8 rounded-full transition-all ${
                  s.notification_popup_enabled ? "bg-emerald-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-all ${
                    s.notification_popup_enabled ? "left-7" : "left-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </Card>

        <Card title="Home Posters (Sliders)">
          <p className="text-xs text-slate-500 mb-3">Up to 5 posters shown on the user app dashboard. Upload a file (tap the thumbnail) or paste an image URL. 16:8 ratio recommended.</p>
          {posters.map((p, i) => (
            <div key={i} className="flex gap-2 items-center mb-2" data-testid={`poster-row-${i}`}>
              <label className="w-12 h-12 rounded-md bg-slate-100 overflow-hidden shrink-0 cursor-pointer hover:bg-slate-200 transition flex items-center justify-center" title="Upload image">
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid={`poster-file-${i}`}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3MB"); return; }
                    const reader = new FileReader();
                    reader.onload = async () => {
                      try {
                        const { data } = await api.post("/admin/upload", { data_url: reader.result, filename: f.name });
                        const next = [...posters];
                        next[i] = { ...next[i], image_url: reader.result, file_id: data.id };
                        setPosters(next);
                        toast.success("Image attached");
                      } catch (err) { toast.error(formatApiError(err)); }
                    };
                    reader.readAsDataURL(f);
                  }}
                />
              </label>
              <Input placeholder="…or paste Image URL" value={p.image_url?.startsWith("data:") ? "(uploaded image)" : (p.image_url || "")} onChange={(e)=>{ const next=[...posters]; next[i]={...next[i], image_url: e.target.value}; setPosters(next); }} className="flex-1" data-testid={`poster-url-${i}`} disabled={p.image_url?.startsWith("data:")} />
              <Input placeholder="Click link (optional)" value={p.link || ""} onChange={(e)=>{ const next=[...posters]; next[i]={...next[i], link: e.target.value}; setPosters(next); }} className="flex-1" />
              <button onClick={()=>setPosters(posters.filter((_,j)=>j!==i))} className="p-2 text-rose-500 hover:bg-rose-50 rounded-md" data-testid={`poster-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {posters.length < 5 && (
            <Button onClick={()=>setPosters([...posters, { image_url: "", link: "" }])} variant="outline" className="border-dashed border-slate-300 w-full h-10 mt-1" data-testid="poster-add"><Plus className="w-4 h-4 mr-1" /> Add Poster</Button>
          )}
          <Button onClick={savePosters} className="btn-brand h-10 w-full mt-3" data-testid="posters-save">Save Posters</Button>
        </Card>

        <Card title="Game Rates" className="lg:col-span-2">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rates-grid">
            {Object.entries(rates).map(([key, rate]) => (
              <RateRow key={key} k={key} rate={rate} onSave={(v) => save({ game_rates: { ...rates, [key]: parseFloat(v) } })} />
            ))}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 ${className}`}>
      <div className="font-display font-semibold text-slate-900 mb-4">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onSave, testid, type = "text" }) {
  const [v, setV] = useState(value);
  useEffect(()=>setV(value), [value]);
  return (
    <div className="mb-3">
      <Label className="text-xs uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input data-testid={`set-${testid}`} type={type} value={v} onChange={(e)=>setV(e.target.value)} className="flex-1" />
        <Button data-testid={`save-${testid}`} onClick={()=>onSave(v)} className="btn-brand h-10 px-4">Save</Button>
      </div>
    </div>
  );
}

function VideoField({ label, value, onSave, testid }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Max 25MB — chhoti video daalein"); return; }
    setUploading(true);
    setProgress(10);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onprogress = (e) => { if (e.total) setProgress(Math.min(70, 10 + Math.round((e.loaded / e.total) * 60))); };
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      setProgress(80);
      const { data } = await api.post("/admin/upload", { data_url: b64, filename: file.name });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const videoUrl = `${backend}/api/files/${data.id}/raw`;
      setProgress(100);
      onSave(videoUrl);
      toast.success(`✅ ${label} uploaded — users ko dikhna shuru!`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const remove = () => {
    if (!window.confirm("Video hata dein?")) return;
    onSave("");
    toast.success("Video removed");
  };

  const hasVideo = !!value;

  return (
    <div className="mb-4 border-2 border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-slate-50">
      <div className="flex items-start justify-between mb-2">
        <Label className="text-sm font-bold text-slate-800">{label}</Label>
        {hasVideo && (
          <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`${testid}-status`}>✓ UPLOADED</span>
        )}
      </div>

      {/* Video preview if already uploaded */}
      {hasVideo && !uploading && (
        <div className="mb-3 rounded-lg overflow-hidden bg-black">
          <video src={value} controls playsInline preload="metadata" className="w-full aspect-video object-contain bg-black" data-testid={`${testid}-preview`} />
        </div>
      )}

      {/* Upload progress bar */}
      {uploading && (
        <div className="mb-3 w-full h-2.5 bg-slate-200 rounded-full overflow-hidden" data-testid={`${testid}-progress`}>
          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        data-testid={`upload-${testid}`}
        disabled={uploading}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
      />

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`btn-upload-${testid}`}
          className="flex-1 h-11 bg-blue-950 hover:bg-blue-900 text-yellow-300 font-bold text-sm tracking-wider"
        >
          {uploading ? `Uploading… ${progress}%` : hasVideo ? "📁 Replace Video" : "📁 Upload Video File"}
        </Button>
        {hasVideo && !uploading && (
          <Button type="button" onClick={remove} data-testid={`btn-remove-${testid}`} variant="outline" className="h-11 px-3 text-rose-600 border-rose-200 hover:bg-rose-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">Format: MP4 / WebM / MOV · Max 25MB · Upload ke turant baad app me live ho jayega</p>
    </div>
  );
}

function RateRow({ k, rate, onSave }) {
  const [v, setV] = useState(rate);
  useEffect(()=>setV(rate), [rate]);
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`rate-${k}`}>
      <div className="text-xs font-medium text-slate-600 mb-1">{k}</div>
      <div className="flex gap-2">
        <Input type="number" step="0.1" value={v} onChange={(e)=>setV(e.target.value)} className="flex-1 h-9" />
        <Button onClick={()=>onSave(v)} className="h-9 text-xs px-3 btn-brand">Save</Button>
      </div>
    </div>
  );
}
